import { useState } from 'react';
import { useOne } from '@refinedev/core';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Button, Modal, message } from 'antd';
import { CapabilityGate, conferenceCopy, DetailPage, useUrlTabState } from '@viq-admin/ui';
import type { Conference } from '../../api/types';
import { archiveConference, restoreConference } from '../../api/archive';
import { CONFERENCE_ARCHIVE_ENABLED } from '../../config/features';
import { ConferenceEdit } from './edit';
import { ConferenceOverview } from '../../views/ConferenceDetail/Overview';
import { ConferenceAssignments } from '../../views/ConferenceDetail/Assignments';
import { ConferenceDiscovery } from '../../views/ConferenceDetail/Discovery';
import { ConferenceResponses } from '../../views/ConferenceDetail/Responses';

const TAB_KEYS = ['overview', 'assignments', 'discovery', 'responses'];

/**
 * Conference detail — a tabbed shell (Overview / Questions / Responses) on
 * the shared DetailPage, with edit folded IN-PLACE (the previously-standalone
 * `/conferences/:id/edit` route now redirects here — see App.tsx). Reads the
 * record via Refine v6 `useOne`, which returns `{ query, result }` — `result`
 * is the unwrapped record. Mirrors the just-landed console `ConferenceShow`'s
 * shell composition byte-for-byte in shape; only the tab set (3, not 5: no
 * Assignments/Toolkit), the fetch import paths, and the capability gate
 * mechanism (portal role claim vs console CASL) differ.
 *
 * This REPLACES the previous bespoke `<Space>` + `<Tabs defaultActiveKey>`
 * shell (which hand-rolled its `?tab=` sync via `window.location.search` +
 * `navigate('?...', { replace: true })`) with the shared `useUrlTabState`
 * helper — parity with the console, which already used it. Net-new behavior
 * this ADDS: `loading > notFound > error` precedence, so a 404 id now shows
 * the Not-found Result instead of an empty "Conference" title + tabs (the
 * previous shell never handled the not-found case at all).
 *
 * The active/archived split still lives on ONE route (`/conferences/:id`):
 * an archived record shows Restore instead of Archive, and both the back
 * button and the "Conferences" breadcrumb crumb target `/conferences/archived`
 * instead of `/conferences` — preserving today's archived-aware navigation.
 *
 * Archive/Restore was previously shown UNGATED (`conference &&`); it is now
 * wrapped `CapabilityGate cap="manage:Conference"` for parity with the
 * console's gated header action. Behavior-preserving: `manage:Conference`
 * maps to `['company_admin']` (see portalCapabilities.ts) and this route is
 * already `RoleGuard(['company_admin'])`-only in App.tsx, so every role that
 * can reach this page already has the capability.
 *
 * The three tab bodies (Overview/Questions/Responses) are reused UNCHANGED
 * from views/ConferenceDetail — they self-fetch and own their own internal
 * gating/loading; only this shell composition changed.
 */
export function ConferenceShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);

  const { result: conference, query } = useOne<Conference>({
    resource: 'conferences',
    id,
    queryOptions: { enabled: !!id },
  });

  const [activeTab, onTabChange] = useUrlTabState(searchParams, setSearchParams, {
    keys: TAB_KEYS,
    defaultKey: 'overview',
  });

  if (!id) return null;

  const error = query?.error as { statusCode?: number } | null | undefined;
  const notFound = error?.statusCode === 404;
  const isArchived = conference?.archivedAt != null;
  const backList = conference?.archivedAt ? '/conferences/archived' : '/conferences';

  function handleArchive() {
    Modal.confirm({
      title: conferenceCopy.archiveTitle,
      content: conferenceCopy.archiveBody,
      okText: 'Archive',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await archiveConference(id!);
          message.success('Conference archived.');
          navigate('/conferences/archived');
        } catch (err: unknown) {
          message.error(
            (err as { message?: string }).message ?? 'Archive failed',
          );
        }
      },
    });
  }

  async function handleRestore() {
    try {
      await restoreConference(id!);
      message.success('Conference restored.');
      navigate('/conferences');
    } catch (err: unknown) {
      message.error((err as { message?: string }).message ?? 'Restore failed');
    }
  }

  const archiveAction = (
    <CapabilityGate cap="manage:Conference">
      <Button danger={!isArchived} onClick={isArchived ? handleRestore : handleArchive}>
        {isArchived ? 'Restore' : 'Archive'}
      </Button>
    </CapabilityGate>
  );

  return (
    <DetailPage
      title={conference?.name ?? 'Conference'}
      breadcrumbs={[
        { label: 'Conferences', href: backList },
        { label: conference?.name ?? '' },
      ]}
      onNavigate={({ href }) => navigate(href)}
      onBack={() => navigate(backList)}
      loading={query?.isLoading}
      error={notFound ? undefined : query?.error}
      notFound={notFound}
      notFoundHref={backList}
      editing={editing}
      onEditingChange={(next) => {
        setEditing(next);
        // Leaving edit by ANY path (Cancel, back, a breadcrumb) must clear
        // `dirty` — not just the `onSaved` success path below. Otherwise a
        // dirty-then-discarded session leaves `dirty` stuck true, and the
        // NEXT edit session (freshly remounted, genuinely pristine) still
        // trips the discard guard on its first Cancel.
        if (!next) setDirty(false);
      }}
      editCap="manage:Conference"
      dirty={dirty}
      actions={CONFERENCE_ARCHIVE_ENABLED ? archiveAction : undefined}
      readView={null}
      editView={
        <ConferenceEdit
          id={id}
          onDirtyChange={setDirty}
          onSaved={() => {
            setDirty(false);
            setEditing(false);
          }}
        />
      }
      tabs={[
        {
          key: 'overview',
          label: 'Overview',
          children: <ConferenceOverview conferenceId={id} />,
        },
        {
          key: 'assignments',
          label: 'Assignments',
          // company_admin only (same cap the console Assignments tab uses).
          cap: 'manage:Conference',
          children: <ConferenceAssignments conferenceId={id} />,
        },
        {
          key: 'discovery',
          label: 'Questions',
          children: <ConferenceDiscovery conferenceId={id} />,
        },
        {
          key: 'responses',
          label: 'Responses',
          children: <ConferenceResponses conferenceId={id} />,
        },
      ]}
      activeTab={activeTab}
      onTabChange={onTabChange}
    />
  );
}
